import { Tool } from './base'
import {
  makeRenderer,
  InlineGroups,
  RenderContext,
  normalizeList,
  scalarToString,
} from './render/render-kit'
import { DatasourceMap } from './render/rtd/datasource.types'
import { AstPredicate, ValueOrList } from './render/types'

// Fields that must NOT be wrapped in double quotes for equality in CB Response.
// Primary case: `ipaddr:"1.2.3.4"` fails; only bare `ipaddr:1.2.3.4` works.
// CB Response maps dest_ip / src_ip / dns_answer → `ipaddr`.
const BARE_VALUE_FIELDS = new Set<string>(['ipaddr'])

// Fields where a SINGLE backslash is the correct escape for value paths.
// CB Response's empirical rule: path / regmod / filemod / modload reject
// doubled backslashes (`path:Downloads\\rtd.exe*` fails) while bare `\` works.
// cmdline, conversely, requires doubled `\\`. All fields still escape spaces
// as `\ `.
const SINGLE_ESCAPE_FIELDS = new Set<string>([
  'path',
  'filemod',
  'modload',
  'regmod',
])

// CB Response supported hunt categories. Unsupported (and intentionally
// omitted): system, security, authentication_events, pipe_created. The tool
// emits a bare Lucene body with no datasource prelude or DataDisplay; entries
// exist so renderSingleSourceGrouped exposes these categories in the UI,
// and to prune CCM predicates that CB Response cannot actually search.
//
// Per-category caveats:
// - network_connection: only IP searching (shared ipaddr field); no
//   domain / url / dns_query support.
// - file_event / process_creation: md5 and sha256 both map to the same CB
//   hash search term, handled via field-level aliasing rather than here.
//
// unsupportedFields for non-core categories blacklists "generic leak" fields
// (process_*/target_path/hashes/registry_*) that don't belong in each event
// type. Without a datasource prelude these would OR into queries and duplicate
// process_creation/file_event output. process_creation and file_event stay
// empty — they are the categories the generic fields actually belong to.
const NON_CORE_BLACKLIST = {
  registry_event: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'md5',
    'sha256',
  ],
  image_load: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'registry_path',
    'registry_key_name',
    'registry_value_name',
  ],
  dns: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'md5',
    'sha256',
    'registry_path',
    'registry_key_name',
    'registry_value_name',
  ],
  network_connection: [
    'process_path',
    'process_name',
    'process_cmdline',
    'target_path',
    'md5',
    'sha256',
    'registry_path',
    'registry_key_name',
    'registry_value_name',
  ],
}

const carbonBlackResponseMap: DatasourceMap = {
  'CarbonBlack Response': {
    any: {
      process_creation: {},
      file_event: {},
      registry_event: { unsupportedFields: NON_CORE_BLACKLIST.registry_event },
      image_load: { unsupportedFields: NON_CORE_BLACKLIST.image_load },
      dns: {
        unsupportedFields: [...NON_CORE_BLACKLIST.dns, 'dns_answer'],
      },
      network_connection: {
        unsupportedFields: [...NON_CORE_BLACKLIST.network_connection, 'url'],
      },
    },
  },
}

export class CarbonBlackResponseTool extends Tool {
  public readonly toolName = 'CarbonBlack Response'
  public readonly toolKind = 'edr' as const
  public override readonly datasourceMap = carbonBlackResponseMap

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const carbonBlackResponseSearchURI = '#/search?rows=10&q='
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${rootURL}${carbonBlackResponseSearchURI}${encodedRenderedContent}`
  }

  public pivotEscape(key: string, value: string): string {
    // Special-case for process_cmdline field
    if (key === 'process_cmdline') {
      return value.replace(/[\\?"()[\]{}*]/g, '\\$&').replace(/[<>]/g, ' ')
    }

    // Default escape for all other fields
    return value
      .replace(/[*$|/"'()[\]{}<>?]/g, '\\$&') // escape special chars
      .replace(/ /g, '\\ ') // escape space as \
      .replace(/[<>]/g, ' ') // replace < and > with space
  }

  /**
   * Carbon Black Response (EDR) renderer (Lucene-like)
   *
   * Grouping
   * - InlineGroups: no root parens; nested parens as needed.
   *
   * Field mapping
   * - toolTuningFields["carbonblack_response"]; no Eta wrapping.
   *
   * Negation
   * - Uses leading minus: `-field:value`.
   * - For multi-clause negation, wrap the whole clause: `-(field:a OR field:b)`.
   *
   * Quoting & escaping
   * - ONLY "equals" is quoted: `field:"value"`.
   * - All other ops are UNQUOTED; spaces in values MUST be escaped as `\ `.
   * - Backslashes are escaped (`\` → `\\`) in all cases.
   *
   * Operators
   * - eq:              field:"v"            | multi: (field:"v1" OR field:"v2")
   * - neq:             -field:"v"           | multi: -(field:"v1" OR field:"v2")
   * - contains:        field:*v*            | multi: (field:*v1* OR field:*v2*)
   * - doesnotcontain:  -field:*v*           | multi: -(field:*v1* OR field:*v2*)
   * - startswith:      field:v*             | multi: (field:v1* OR field:v2*)
   * - endswith:        field:*v             | multi: (field:*v1 OR field:*v2)
   * - lt/lte/gt/gte:   range:
   *      lt  -> field:{* TO v}
   *      lte -> field:[* TO v]
   *      gt  -> field:{v TO *}
   *      gte -> field:[v TO *]
   *   (range bounds are UNQUOTED; spaces escaped)
   *
   * Null/Empty
   * - isnull     → -field:*
   * - isnotnull  → field:*
   * - isempty    → field:""
   * - isnotempty → -field:""
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => f,
      andGlue: ' AND ',
      orGlue: ' OR ',
      // renderContext.quote is used only for equals; leave default (double-quoted with escaping)
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const f = renderContext.wrapField(renderContext.mapField(p.field))

      // Keep raw to preserve numbers
      const raw: ValueOrList | undefined = p.value
      const rawList =
        raw == null ? [undefined] : Array.isArray(raw) ? raw : [raw]

      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Per-field escape policy — see SINGLE_ESCAPE_FIELDS docstring above.
      // cmdline (and every non-listed field) double-escapes `\`; path/regmod/
      // filemod/modload leave `\` single. Spaces always escape as `\ `.
      const useSingleEscape = SINGLE_ESCAPE_FIELDS.has(f)
      const escapeBare = (s: string) => {
        const withSlash = useSingleEscape
          ? String(s)
          : String(s).replace(/\\/g, '\\\\')
        return withSlash.replace(/ /g, '\\ ')
      }

      // Wildcard shaping for string-ish ops
      const shape = (op: AstPredicate['op'], s: string) => {
        switch (op) {
          case 'contains':
            return `${s}*`
          case 'doesnotcontain':
            return `${s}*`
          case 'startswith':
            return `${s}*`
          case 'endswith':
            return `*${s}`
          default:
            return s
        }
      }

      // Clause builders
      // Bare-value fields (e.g. netconn_ipv4) reject double-quoted equality in
      // CB Response — use the bare escape variant for those. All other fields
      // use the standard quoted equality.
      const eqClause = (s: string) =>
        BARE_VALUE_FIELDS.has(f)
          ? `${f}:${escapeBare(s)}`
          : `${f}:${renderContext.quote(s)}`
      const bareClause = (s: string) => `${f}:${escapeBare(s)}` // wildcard family

      // Negation helper: single → "-field:term"; multi/complex → "-( ... )"
      const NEG = (clause: string, multi: boolean) =>
        multi ? `-${clause}` : `-${clause}`

      /* ---------- Null / Empty ---------- */
      switch (p.op) {
        case 'isnull':
          return `-${f}:*`
        case 'isnotnull':
          return `${f}:*`
        case 'isempty':
          return `${f}:${renderContext.quote('')}`
        case 'isnotempty':
          return `-${f}:${renderContext.quote('')}`
      }

      /* ---------- Equality / Inequality ---------- */
      // CB Response tokenizes file-path fields (path, filemod, modload, etc.),
      // so a trailing-basename match (endswith `malware.exe`) is equivalent to
      // a token-equality match against the basename token. Rewrite endswith →
      // eq here to avoid the leading-wildcard search `*malware.exe` which is
      // expensive against CB's token index.
      if (p.op === 'eq' || p.op === 'neq' || p.op === 'endswith') {
        const parts = vals.map((v) => eqClause(v))
        if (!isMulti) {
          return p.op === 'neq'
            ? NEG(parts[0], /*multi*/ false)
            : parts[0]
        }
        const joined = `(${parts.join(renderContext.orGlue)})`
        return p.op === 'neq' ? NEG(joined, /*multi*/ true) : joined
      }

      /* ---------- String wildcard family (unquoted) ---------- */
      // endswith is handled above (rewritten to eq); keep the other three here.
      if (
        ['contains', 'doesnotcontain', 'startswith'].includes(p.op)
      ) {
        const parts = vals.map((v) => bareClause(shape(p.op as any, v)))
        if (!isMulti) {
          return p.op === 'doesnotcontain' ? NEG(parts[0], false) : parts[0]
        }
        const joined = `(${parts.join(p.op === 'doesnotcontain' ? renderContext.orGlue : renderContext.orGlue)})`
        // For negative multi, wrap the whole OR group
        return p.op === 'doesnotcontain' ? NEG(joined, true) : joined
      }

      /* comparisons */
      if (['lt', 'lte', 'gt', 'gte'].includes(p.op)) {
        const v0 = escapeBare(vals[0])
        switch (p.op) {
          case 'lt':
            return `${f}:{* TO ${v0}}`
          case 'lte':
            return `${f}:[* TO ${v0}]`
          case 'gt':
            return `${f}:{${v0} TO *}`
          case 'gte':
            return `${f}:[${v0} TO *]`
        }
      }

      /* ---------- Fallback (treat like equality) ---------- */
      if (isMulti) {
        const parts = vals.map((v) => eqClause(v))
        return `(${parts.join(renderContext.orGlue)})`
      }
      return eqClause(vals[0])
    },
  })

  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic. NOTE: Utilize equals/not equals if searching full keyword strings. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'
}
